/** @domain personal -- Person naming and demographics: first/last/middle name, suffix, gender, marital status. */
import { z } from 'zod';
import { createDomainField } from './types';
import { trimExtraSpaces, capitalizeFirst } from './formatters';

const define = createDomainField('personal');

// ---------------------------------------------------------------------------
// Base field — reused by first/last/middle/suffix/fullLegalName
// ---------------------------------------------------------------------------

// Shared label 'Name' with content.name — this is for person names; content.name is for content/template names
export const personName = define({
  label: 'Name',
  schema: z
    .string()
    .max(100, 'Must be at most 100 characters')
    .regex(/^\s*\S[\s\S]*$/, 'Must not be blank')
    .regex(/^[a-z,0-9, @()\-/*"':;_#\-+]+$/i, 'Invalid Name')
    .refine((v) => !v.includes('  '), 'Must not contain double spaces'),
  adapterHint: 'text',
  formatter: [trimExtraSpaces, capitalizeFirst],
  displayFormat: 'text',
});

// ---------------------------------------------------------------------------
// Specific name fields
// ---------------------------------------------------------------------------

export const firstName = define({
  label: 'First Name',
  schema: personName.schema,
  adapterHint: 'text',
  controlWidth: 'long',
  formatter: [trimExtraSpaces, capitalizeFirst],
  displayFormat: 'text',
});

export const lastName = define({
  label: 'Last Name',
  schema: personName.schema,
  adapterHint: 'text',
  controlWidth: 'long',
  formatter: [trimExtraSpaces, capitalizeFirst],
  displayFormat: 'text',
});

export const middleName = define({
  label: 'Middle Name',
  schema: z
    .string()
    .max(100, 'Must be at most 100 characters')
    .regex(/^\s*\S[\s\S]*$/, 'Must not be blank')
    .regex(/^[a-z,0-9, @()\-/*"':;_#\-+]+$/i, 'Invalid Middle Name')
    .refine((v) => !v.includes('  '), 'Must not contain double spaces'),
  adapterHint: 'text',
  controlWidth: 'medium',
  formatter: [trimExtraSpaces, capitalizeFirst],
  displayFormat: 'text',
});

export const suffix = define({
  label: 'Suffix',
  placeholder: 'Jr., Sr., III',
  schema: z.string().max(20, 'Must be at most 20 characters'),
  adapterHint: 'text',
  displayFormat: 'text',
});

export const fullLegalName = define({
  label: 'Full Legal Name',
  placeholder: 'Enter full legal name',
  schema: personName.schema,
  adapterHint: 'text',
  formatter: [trimExtraSpaces, capitalizeFirst],
  displayFormat: 'text',
});

export const gender = define({
  label: 'Gender',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

export const maritalStatus = define({
  label: 'Marital Status',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});
